import { Component, effect, inject, Input, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Workspace, WorkspaceType } from '../../../models/workspace';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { NgIf } from '@angular/common';
import { PaginatorModule } from 'primeng/paginator';
import { WorkspaceStore } from '../../../stores/workspace/workspace.state';
import { ActivatedRoute, Router } from '@angular/router';
import { AppUtilityService } from '../../../utility/app.utility';
import { RoutingService } from '../../../services/routing.service';
import { BaseFormComponent } from '../../molecules/base-form/base-form.component';
import { PermissionService } from '../../../services/permission.service';
import { FormFieldWrapperComponent } from '../../molecules/form-field-wrapper/form-field-wrapper.component';
import { DividerModule } from 'primeng/divider';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { FormFooterComponent } from '../../molecules/form-footer/form-footer.component';
import { getState } from '@ngrx/signals';

@Component({
  selector: 'app-workspace-form',
  standalone: true,
  imports: [
    AutoCompleteModule,
    ButtonModule,
    CardModule,
    CheckboxModule,
    InputTextModule,
    InputTextareaModule,
    NgIf,
    PaginatorModule,
    ReactiveFormsModule,
    FormFieldWrapperComponent,
    DividerModule,
    FormFooterComponent,
  ],
  templateUrl: './workspace-form.component.html',
  styleUrl: './workspace-form.component.scss',
})
export class WorkspaceFormComponent
  extends BaseFormComponent<Workspace>
  implements OnInit
{
  readonly store = inject(WorkspaceStore);
  override form: FormGroup = new FormGroup({});
  override itemToEdit?: Workspace;
  @Input() type?: string;

  override formInitialized = false;

  types: WorkspaceType[] = [
    {
      value: 'indicatorCategory',
      label: 'Indikatorenordner',
      icon: 'fas fa-folder',
    },
    { value: 'dataLayerType', label: 'Räume', icon: 'fa-regular fa-map' },
    {
      value: 'poiDataLayerType',
      label: 'Standorte',
      icon: 'fas fa-location-dot',
    },
    {
      value: 'bookmarkCategory',
      label: 'Themenbereiche',
      icon: 'fas fa-layer-group',
    },
    { value: 'topic', label: 'Themen', icon: 'fas fa-book-open' },
  ];

  constructor(
    router: Router,
    route: ActivatedRoute,
    appUtilityService: AppUtilityService,
    routingService: RoutingService,
    permissionService: PermissionService,
    confirmationDialogService: ConfirmationDialogService,
    private fb: FormBuilder
  ) {
    super(
      router,
      route,
      appUtilityService,
      routingService,
      permissionService,
      confirmationDialogService
    );

    effect(() => {
      const state = getState(this.store);
      if (state.selectedId) {
        this.initializeForm();
      }
    });
  }

  /**
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * This method enhances the initialization by ensuring a specific form setup is executed if not already initialized.
   *
   * @return {void} Does not return a value.
   */
  override ngOnInit(): void {
    super.ngOnInit();
    if (!this.formInitialized) this.initializeForm();
  }

  /**
   * Initializes the form by setting up form controls and their default values based on the selected item.
   * Also, sets the form as initialized once configuration is complete.
   *
   * @return {void} Does not return any value.
   */
  initializeForm(): void {
    this.itemToEdit = this.store.selectedItem();
    this.form = this.fb.group({
      name: [this.itemToEdit?.name || '', Validators.required],
      type: [this.itemToEdit?.type || this.type || '', Validators.required],
    });
    this.formInitialized = true;
  }

  /**
   * Handles the submission process by invoking the submit action.
   * This method is typically triggered by a user event, such as a form submission.
   *
   * @return {void} This method does not return a value.
   */
  onSubmit(): void {
    this.submit();
  }
}
